/* eslint-disable unicorn/prevent-abbreviations */
import { isDefined } from "@clipboard-health/util-ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { type DocumentStatus } from "../../../../../Accounts/Documents/types";
import { APP_V2_USER_EVENTS, logEvent } from "../../../../../lib/analytics";
import { useDefinedWorker } from "../../../../../Worker/useDefinedWorker";
import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import { ESignWorkflowSessionStatus } from "./constants";
import { getDocumentStatusOfESignWorkflowSession } from "./getDocumentStatusOfESignWorkflowSession";
import { type ESignWorkflowSessionAttributes } from "./types";
import { useSignableESignWorkflowSession } from "./useSignableESignWorkflowSession";
import { useUpdateESignWorkflowSession } from "./useUpdateESignWorkflowSession";

export interface CompleteSigningResult {
  documentStatus: DocumentStatus | undefined;
  quizResult: ESignWorkflowSessionAttributes["quizResult"];
}

interface UsePandaDocSigningSessionParams {
  requirementId: string;
  signType: string;
  analyticsMetadata?: Record<string, unknown>;
  onSessionLoadError: (error: unknown) => void;
}

interface UsePandaDocSigningSessionResult {
  signableESignWorkflowSession: ReturnType<typeof useSignableESignWorkflowSession>["data"];
  isLoadingPandaDoc: boolean;
  isLoadingSession: boolean;
  startSigning: (params?: { compositeParentRequirementId?: string }) => void;
  handleDocumentLoaded: () => void;
  completeSigningAndGetDocumentStatus: () => Promise<CompleteSigningResult>;
}

export function usePandaDocSigningSession(
  params: UsePandaDocSigningSessionParams
): UsePandaDocSigningSessionResult {
  const { requirementId, signType, analyticsMetadata, onSessionLoadError } = params;

  const worker = useDefinedWorker();
  const workerId = worker.userId;

  const queryClient = useQueryClient();

  const [isLoadingPandaDoc, setIsLoadingPandaDoc] = useState(false);
  const pandaDocSignCtaClickedAtRef = useRef<number>();

  const {
    mutate: loadSignableESignWorkflowSession,
    data: signableESignWorkflowSession,
    isLoading: isLoadingSession,
  } = useSignableESignWorkflowSession({
    requirementId,
    onError: (error) => {
      setIsLoadingPandaDoc(false);
      onSessionLoadError(error);
    },
  });

  const { mutateAsync: updateESignWorkflowSession } = useUpdateESignWorkflowSession();

  const signableSessionRef = useRef(signableESignWorkflowSession);
  signableSessionRef.current = signableESignWorkflowSession;

  const startSigning = useCallback(
    (startParams?: { compositeParentRequirementId?: string }) => {
      setIsLoadingPandaDoc(true);
      pandaDocSignCtaClickedAtRef.current = Date.now();
      loadSignableESignWorkflowSession({
        compositeParentRequirementId: startParams?.compositeParentRequirementId,
      });
      logEvent(APP_V2_USER_EVENTS.PANDADOC_SIGN_CTA_CLICKED, {
        requirementId,
        signType,
        ...analyticsMetadata,
      });
    },
    [loadSignableESignWorkflowSession, requirementId, signType, analyticsMetadata]
  );

  const handleDocumentLoaded = useCallback(() => {
    setIsLoadingPandaDoc(false);
    const timeToLoadInMilliseconds = pandaDocSignCtaClickedAtRef.current
      ? Date.now() - pandaDocSignCtaClickedAtRef.current
      : undefined;
    logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_LOADED, {
      requirementId,
      signType,
      timeToLoadInMilliseconds,
      ...analyticsMetadata,
    });
    const session = signableSessionRef.current;
    if (isDefined(session)) {
      void updateESignWorkflowSession({
        requirementId,
        sessionId: session.id,
        attributesToUpdate: { status: ESignWorkflowSessionStatus.START },
      });
    }
  }, [requirementId, signType, analyticsMetadata, updateESignWorkflowSession]);

  const completeSigningAndGetDocumentStatus = useCallback(async () => {
    const session = signableSessionRef.current;
    if (!isDefined(session)) {
      throw new Error("signableESignWorkflowSession is not defined");
    }

    const updatedSessionResponse = await updateESignWorkflowSession({
      requirementId,
      sessionId: session.id,
      attributesToUpdate: { status: ESignWorkflowSessionStatus.SIGNED },
    });

    void invalidateWorkerRequirements(queryClient);

    const quizResult = updatedSessionResponse?.data.attributes.quizResult;

    const documentStatus = updatedSessionResponse
      ? await getDocumentStatusOfESignWorkflowSession({
          data: updatedSessionResponse.data,
          included: updatedSessionResponse.included,
        })
      : await getDocumentStatusOfESignWorkflowSession({
          requirementId,
          workerId,
        });

    return { documentStatus, quizResult };
  }, [requirementId, workerId, updateESignWorkflowSession, queryClient]);

  return {
    signableESignWorkflowSession,
    isLoadingPandaDoc,
    isLoadingSession,
    startSigning,
    handleDocumentLoaded,
    completeSigningAndGetDocumentStatus,
  };
}
/* eslint-enable unicorn/prevent-abbreviations */
